// FilterModificationHistoryMenu.tsx
import { FormControl, InputLabel, Select, Stack } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import React, { useState } from 'react'
import { t } from 'i18next'
import FilterMenu from './FilterMenu'
import { ModificationHistoryParams, OperationType } from '../../store/redux/reducers/auditSlice'
import { isEqual } from 'lodash'
import { Dayjs } from 'dayjs'
import DateFormat from '../../store/types/enums/DateFormat'
import { useLeftPadding } from '../../store/redux/hooks'
import DateRangePickerWithPresets from '../DateRangePickerWithPresets/DateRangePickerWithPresets'

type FilterProps = {
  requestParams: ModificationHistoryParams
  setRequestParams: React.Dispatch<React.SetStateAction<ModificationHistoryParams>>
  selectedDate: Dayjs
}

const FilterModificationHistoryMenu = ({ requestParams, setRequestParams, selectedDate }: FilterProps) => {
  const leftPadding = useLeftPadding()
  const [operationType, setOperationType] = useState<OperationType | undefined>(requestParams.operationType)
  const [startDate, setStartDate] = useState<Dayjs | null>(null)
  const [endDate, setEndDate] = useState<Dayjs | null>(null)

  const handleFilter = () => {
    const newProps: ModificationHistoryParams = {
      ...requestParams,
      operationType: operationType || undefined,
      startDate: startDate?.format(DateFormat.YearMonthDay) || requestParams.startDate,
      endDate: endDate?.format(DateFormat.YearMonthDay) || requestParams.endDate,
      page: 0,
    }

    if (!isEqual(newProps, requestParams)) setRequestParams(newProps)
  }

  const handleClear = () => {
    const monthStart = selectedDate.startOf('month').format(DateFormat.YearMonthDay)
    const monthEnd = selectedDate.endOf('month').format(DateFormat.YearMonthDay)
    setOperationType(undefined)
    setStartDate(null)
    setEndDate(null)
    setRequestParams({
      'chosen.id': requestParams?.['chosen.id'] || undefined,
      tableName: requestParams.tableName || 'work_hours',
      page: 0,
      size: requestParams.size,
      startDate: monthStart,
      endDate: monthEnd,
    })
  }

  return (
    <FilterMenu clear={handleClear} filter={handleFilter}>
      <Stack sx={{ pt: 1, gap: 2, pl: leftPadding }}>
        <FormControl size="small">
          <InputLabel id="operation-type-label">
            {t('page.schedule.modificationHistory.filter.operationType')}
          </InputLabel>
          <Select
            label={t('page.schedule.modificationHistory.filter.operationType')}
            inputProps={{
              'aria-labelledby': 'operation-type-label',
            }}
            value={operationType || ''}
            onChange={event => setOperationType(event.target.value as OperationType)}
          >
            <MenuItem value="addition">{t('page.modification.addition')}</MenuItem>
            <MenuItem value="modification">{t('page.modification.modification')}</MenuItem>
            <MenuItem value="deletion">{t('page.modification.deletion')}</MenuItem>
            <MenuItem value="acknowledged">{t('page.modification.acknowledged')}</MenuItem>
            <MenuItem value="unacknowledged">{t('page.modification.unacknowledged')}</MenuItem>
          </Select>
        </FormControl>
        <DateRangePickerWithPresets
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          column
        />
      </Stack>
    </FilterMenu>
  )
}

export default FilterModificationHistoryMenu
